/* Theme custom file */

/**
 * Collection filters analytics. Every event goes through window.tbAnalytics, which owns the
 * consent gate, the theme editor guard and the dataLayer queue.
 * Spec: docs/specs/tracking-datalayer.md
 *
 * The active filters of the last collection page visited are kept in sessionStorage, so the
 * product page and the add to cart that follow it can still say which filters led there.
 *
 * Loaded as a module after the theme's import map, it imports nothing: the theme event names
 * are spelled out below. The decisions that read nothing from the page (naming the filters, the
 * stored record, the referrer, the cart event) live in assets/filters-tracking-core.js, a classic
 * script rendered just before this one.
 */

const core = window.tbFiltersTrackingCore;

/** The theme's FilterUpdateEvent and CartUpdateEvent names (assets/events.js). */
const THEME_EVENTS = {
  filterUpdate: 'filter:update',
  cartUpdate: 'cart:update',
};

const EVENTS = {
  drawerOpened: 'web_filters_drawer_opened',
  applied: 'web_filters_applied',
  productViewed: 'web_product_viewed_with_filters',
  productAdded: 'web_product_added_to_cart_with_filters',
};

const PLATFORM = 'site';
const DRAWER_ID = 'filters-drawer-alternate';
const STORAGE_KEY = 'tb_filters_last_collection';

/** @param {string} name @param {Record<string, unknown>} properties */
function track(name, properties) {
  window.tbAnalytics?.track(name, properties);
}

/**
 * @typedef {{
 *   template: string,
 *   collection_handle: string | null,
 *   collection_handles: string[],
 *   operation_id: string | null,
 *   product_id: string | null,
 *   product_handle: string | null,
 *   product_name: string | null,
 *   brand_name: string | null,
 *   filters?: Record<string, { label: string, values: Record<string, string> }>,
 * }} Context
 */

/** @returns {Context | null} The page context rendered by snippets/tb-filters-tracking.liquid. */
function readContext() {
  try {
    return JSON.parse(document.getElementById('tb-filters-context')?.textContent ?? '');
  } catch {
    return null;
  }
}

/**
 * The active filters of the last collection page visited. Every collection page overwrites or
 * removes it, so filters never outlive the collection they were set on.
 * @typedef {{ handle: string, operation_id?: string, filter_names: string }} StoredFilters
 */

/** @returns {string | null} */
function readStoredRaw() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {StoredFilters | null} filters */
function writeStored(filters) {
  try {
    if (filters) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked or full, the events then simply stop following the shopper.
  }
}

/**
 * Labels as displayed, read from the drawer. A value that was active stays known after a render
 * replaces the drawer, so a filter removed from the URL can still be named.
 * @type {Map<string, { filter: string, value: string }>}
 */
const labels = new Map();

/**
 * The Shopify labels of every filter of the collection, by parameter, rendered with the page
 * context. They name the values of the sections the drawer hides, which it holds without labels.
 * @type {NonNullable<Context['filters']>}
 */
let collectionFilters = {};

/** @param {Element} element */
function filterLabelOf(element) {
  return element.closest('accordion-custom')?.querySelector('.facets__label')?.textContent?.trim() ?? '';
}

/** @param {HTMLInputElement} input */
function valueLabelOf(input) {
  const own = input.getAttribute('aria-label') ?? input.dataset.label;
  if (own) return own.trim();
  return (input.id && document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.textContent?.trim()) || '';
}

function scanLabels() {
  const drawer = document.getElementById(DRAWER_ID);
  if (!drawer) return;

  for (const input of drawer.querySelectorAll('input[name^="filter."]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const filter = filterLabelOf(input);
    if (input.name === core.PRICE_MIN_PARAM || input.name === core.PRICE_MAX_PARAM) {
      labels.set(core.labelKey(input.name, ''), { filter, value: '' });
      continue;
    }
    const value = valueLabelOf(input);
    if (filter && value) labels.set(core.labelKey(input.name, input.value), { filter, value });
  }
}

/** @param {URLSearchParams} params */
const filterNames = (params) => core.filterNames(params, labels, collectionFilters);

/** Sends the filter events of a collection page. */
class CollectionFiltersTracking {
  /** @type {Context} */
  #context;

  /** @type {string} */
  #handle;

  #names = '';

  /** @param {Context} context @param {string} handle */
  constructor(context, handle) {
    this.#context = context;
    this.#handle = handle;
  }

  start() {
    collectionFilters = this.#context.filters ?? {};
    scanLabels();
    this.#names = filterNames(new URLSearchParams(window.location.search));
    this.#remember();

    // The event does not bubble. Capture still reaches the document.
    document.addEventListener('dialog:open', (event) => this.#handleDialogOpen(event), true);
    document.addEventListener(THEME_EVENTS.filterUpdate, () => this.#handleFilterUpdate());
    document.addEventListener(THEME_EVENTS.cartUpdate, (event) => this.#handleCartUpdate(event));
  }

  #commonProperties() {
    return {
      operation_id: this.#context.operation_id ?? undefined,
      platform: PLATFORM,
      collection_handle: this.#handle,
      filter_names: this.#names,
      filters_count: core.countOf(this.#names),
    };
  }

  /** Overwrites the record of the previous collection, or removes it when no filter is active. */
  #remember() {
    writeStored(
      this.#names
        ? { handle: this.#handle, operation_id: this.#context.operation_id ?? undefined, filter_names: this.#names }
        : null
    );
  }

  /** @param {Event} event */
  #handleDialogOpen(event) {
    if (!(event.target instanceof Element) || event.target.id !== DRAWER_ID) return;

    scanLabels();
    track(EVENTS.drawerOpened, this.#commonProperties());
  }

  /**
   * Fires for every change of the form, the sort included, so only a change of the filters
   * is reported. The URL is read rather than the event's parameters: both dispatch paths push
   * the new URL first, while the event of a URL change (reset, a removed filter, a category
   * pill) still carries the form's previous state. The drawer still holds the values the
   * shopper just touched, and the label cache keeps the ones that were removed.
   */
  #handleFilterUpdate() {
    scanLabels();
    const names = filterNames(new URLSearchParams(window.location.search));
    if (names === this.#names) return;

    this.#names = names;
    this.#remember();
    // Clearing every filter is not reported, the app does not report it either.
    if (!names) return;
    track(EVENTS.applied, this.#commonProperties());
  }

  /**
   * An add made from the grid, the quick add included, while filters are active.
   * @param {Event} event
   */
  #handleCartUpdate(event) {
    if (!this.#names) return;

    const added = cartAddition(event);
    if (added) track(EVENTS.productAdded, { ...this.#commonProperties(), ...added });
  }
}

/**
 * The product and variant of a successful add to cart, or null for any other cart update.
 * @param {Event} event
 */
function cartAddition(event) {
  const form = event.target instanceof Element ? event.target : null;
  return core.cartAddition(
    /** @type {CustomEvent} */ (event).detail,
    () => /** @type {HTMLInputElement | null | undefined} */ (form?.querySelector('input[name="id"]'))?.value
  );
}

/** Sends the filter events of a product page reached from a filtered collection. */
class ProductFiltersTracking {
  /** @type {Context} */
  #context;

  /** @type {StoredFilters} */
  #stored;

  /** @type {string} */
  #handle;

  /** @param {Context} context @param {StoredFilters} stored @param {string} handle */
  constructor(context, stored, handle) {
    this.#context = context;
    this.#stored = stored;
    this.#handle = handle;
  }

  start() {
    track(EVENTS.productViewed, this.#properties());
    document.addEventListener(THEME_EVENTS.cartUpdate, (event) => this.#handleCartUpdate(event));
  }

  #properties() {
    const context = this.#context;
    // The page's own collection may not be the filtered one, its operation then does not apply.
    const contextOperationId = this.#handle === context.collection_handle ? context.operation_id : null;
    return {
      operation_id: this.#stored.operation_id ?? contextOperationId ?? undefined,
      platform: PLATFORM,
      collection_handle: this.#handle,
      filter_names: this.#stored.filter_names,
      filters_count: core.countOf(this.#stored.filter_names),
      product_id: context.product_id ?? undefined,
      product_handle: context.product_handle ?? undefined,
      product_name: context.product_name ?? undefined,
      brand_name: context.brand_name ?? undefined,
    };
  }

  /**
   * Adds of other products shown on the page, the recommendations for instance, are not credited
   * to the filters.
   * @param {Event} event
   */
  #handleCartUpdate(event) {
    const added = cartAddition(event);
    if (added?.product_id === this.#context.product_id) {
      track(EVENTS.productAdded, { ...this.#properties(), variant_id: added.variant_id });
    }
  }
}

/**
 * The filters of the last collection visited, when the visitor comes from that collection's page
 * and the product belongs to it.
 * @param {Context} context
 * @returns {{ handle: string, stored: StoredFilters } | null}
 */
const findStoredFilters = (context) =>
  core.findStoredFilters(context, readStoredRaw(), document.referrer, window.location.origin);

const context = readContext();

if (!core || !context) {
  // The core script did not load, or the page carries no context: nothing to report.
} else if (context.template === 'collection' && context.collection_handle) {
  new CollectionFiltersTracking(context, context.collection_handle).start();
} else if (context.template === 'product') {
  const found = findStoredFilters(context);
  if (found) new ProductFiltersTracking(context, found.stored, found.handle).start();
}
